import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Stack,
  TextField,
  ThemeProvider,
  createTheme,
} from '@mui/material';
import skypadLogo from './images/skypad_logo.png';
import {
  resolveSsoRoute,
} from './js/auth/ssoRouting';

const theme = createTheme({
  palette: {
    primary: {
      main: '#54B948',
    },
  },
  typography: {
    fontSize: 20,
    fontFamily: '"Segoe UI", Arial, sans-serif',
  },
});

function getAuthErrorAlert() {
  try {
    const params = new URLSearchParams(window.location.search || '');
    const error = params.get('error');
    const errorDescription = params.get('error_description');
    if (!error && !errorDescription) return null;
    const desc = String(errorDescription || '').toLowerCase();
    if (
      error === 'invalid_request' &&
      (desc.indexOf('connection is not enabled') >= 0 ||
        desc.indexOf('connection was not found') >= 0)
    ) {
      return 'Your company has not enabled single sign-on (SSO) for this application. Please enter your SKYPAD username instead.';
    }
    return errorDescription || error || 'Authentication failed. Please try again.';
  } catch {
    return null;
  }
}

const cardSx = {
  px: { xs: 3, sm: 4 },
  py: { xs: 5, sm: 7 },
  borderRadius: 2,
  border: '1px solid #e0e0e0',
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  backgroundColor: '#fff',
};

const outlinedBtnSx = {
  borderRadius: 1.5,
  borderColor: '#54b948',
  color: '#54b948',
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': { borderColor: '#49a93f', color: '#49a93f', backgroundColor: 'rgba(84,185,72,0.05)' },
};

function SkypadLogo() {
  return (
    <Box
      component="img"
      src={skypadLogo}
      alt="SKYPAD"
      sx={{ width: '100%', height: 'auto' }}
    />
  );
}

function LoginEntryPage({ onSubmit, isAuthenticating = false }) {
  const routeHint = useMemo(() => resolveSsoRoute(), []);
  const [identifier, setIdentifier] = useState(
    routeHint && routeHint.loginHint ? routeHint.loginHint : ''
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const authErrorMessage = useMemo(() => getAuthErrorAlert(), []);

  const isBusy = Boolean(isAuthenticating || isSubmitting);
  // Submitting an empty identifier is only meaningful when the URL itself
  // named an explicit connection/organization to redirect to — the client no
  // longer holds any tenant list to resolve a bare visit against (removed by
  // the 2026-08 security fix), so there is no other case left where a blank
  // field should be allowed through.
  const hasRouteSso = Boolean(routeHint.explicitConnection || routeHint.explicitOrganization);
  // Every email now resolves to mode "deferred" — never "basic" (only a
  // non-email identifier can be, and this check requires isEmail anyway) —
  // because the client no longer holds any domain list (removed by the
  // 2026-08 security fix) and so can never rule out an address being SSO.
  // There is therefore no pre-submit condition left that should disable the
  // button or warn for an email; /api/auth/check-sso is the only thing that
  // can say so, and it does that by throwing from onSubmit — handleSubmit
  // catches it into errorMessage and renders it inline above the field.
  const isInvalidEmail =
    identifier.indexOf('@') >= 0 &&
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedIdentifier = identifier.trim();
    if (!trimmedIdentifier && !hasRouteSso) {
      setErrorMessage('Enter your username or email to continue.');
      return;
    }
    setErrorMessage('');
    setIsSubmitting(true);
    try {
      await onSubmit(trimmedIdentifier);
      setIsSubmitting(false);
    } catch (error) {
      setErrorMessage(
        error && error.message
          ? error.message
          : "We couldn't start login. Please try again."
      );
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = isBusy || isInvalidEmail;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', backgroundColor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ py: 4, width: '100%', maxWidth: 400, mx: 'auto', px: 2 }}>
          <Card component="form" onSubmit={handleSubmit} sx={cardSx}>
            <Stack spacing={2.5}>
              <Box sx={{ textAlign: 'center', pb: 0.5 }}>
                <SkypadLogo />
              </Box>

              <div>
                <p style={{ margin: 0, color: '#333', lineHeight: 1.7, fontSize: '1.25rem' }}>
                  <span style={{ fontWeight: 700 }}>SSO clients:</span>{' Enter your company email address'}
                </p>
                <p style={{ margin: 0, color: '#333', lineHeight: 1.7, fontSize: '1.25rem' }}>
                  <span style={{ fontWeight: 700 }}>Non-SSO clients:</span>{' Enter your SKYPAD username'}
                </p>
              </div>

              {authErrorMessage && (
                <>
                  <Alert severity="warning">{authErrorMessage}</Alert>
                  <Button fullWidth variant="outlined" onClick={() => window.location.replace('/')} sx={outlinedBtnSx}>
                    Back
                  </Button>
                </>
              )}
              {!authErrorMessage && errorMessage && <Alert severity="error">{errorMessage}</Alert>}
              {!authErrorMessage && isInvalidEmail && (
                <Alert severity="error">Enter a valid email address.</Alert>
              )}

              {!authErrorMessage && (
                <>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Company email or username*"
                    value={identifier}
                    onChange={(e) => {
                      setIdentifier(e.target.value);
                      if (errorMessage) setErrorMessage('');
                    }}
                    disabled={isBusy}
                    autoComplete="username"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disableElevation
                    fullWidth
                    disabled={isSubmitDisabled}
                    sx={{
                      py: 1.4,
                      borderRadius: 1.5,
                      backgroundColor: '#54b948',
                      color: '#fff',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:hover': { backgroundColor: '#49a93f' },
                      '&.Mui-disabled': { backgroundColor: '#a5d6a0', color: '#fff' },
                    }}
                  >
                    {isBusy ? 'Continuing…' : 'Continue'}
                  </Button>
                </>
              )}
            </Stack>
          </Card>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default LoginEntryPage;
